import { useEffect, useMemo, useRef, useState } from "react";
import {
  INTENSITY_PALETTE,
  formatValue,
  intensityColor,
  type ValueFormat,
} from "../lib";
import type { WidgetDataRow } from "../types";
import styles from "./WidgetRenderer.module.css";

/**
 * 2D categorical heatmap rendered as raw SVG.
 *
 * Recharts doesn't ship a real matrix heatmap, so this component owns
 * the layout (axis label gutters, square-ish cells, 1px gaps), the
 * color encoding (delegated to `intensityColor` from the shared lib),
 * and the hover tooltip (a CSS-positioned floating div driven by
 * pointermove on each cell).
 *
 * Axis ordering follows the SQL result row order — dashboard authors
 * are expected to ORDER BY the natural ordering of their bins (e.g.
 * `extract(dow ...)` for weekdays, `0..23` for hours-of-day) so the
 * resulting heatmap reads top-to-bottom and left-to-right exactly as
 * the SELECT did.
 *
 * Class names are intentionally prefixed `matrixHeatmap*` to avoid
 * colliding with the calendar-style heatmap that lives next to this
 * file — they share `intensityColor` / `INTENSITY_PALETTE` from the
 * shared lib but want different layout primitives.
 */

const Y_AXIS_WIDTH = 92;
const RIGHT_PAD = 12;
const TOP_PAD = 8;
const X_LABEL_MARGIN = 12;
const X_LABEL_HEIGHT = 28;
const X_LABEL_HEIGHT_ROTATED = 56;
const CELL_GAP = 1;
const ROTATE_THRESHOLD = 8;
const DEFAULT_MIN_CELL = 40;
const MAX_CELL_HEIGHT = 56;
const MIN_CELL_HEIGHT = 20;
const FALLBACK_WIDTH = 640;
const TOOLTIP_WIDTH = 220;
const ROW_LABEL_MAX_CHARS = 16;
const COL_LABEL_MAX_CHARS = 16;

export interface MatrixHeatmapProps {
  rows: WidgetDataRow[];
  xKey: string;
  yKey: string;
  valueKey: string;
  valueFormat: ValueFormat;
  cellMinWidth?: number;
}

interface CellInfo {
  row: string;
  col: string;
  value: number | null;
  display: unknown;
}

interface HoverState {
  cell: CellInfo;
  x: number;
  y: number;
}

function toCategoryKey(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function truncate(label: string, max: number): string {
  if (label.length <= max) return label;
  return `${label.slice(0, Math.max(1, max - 1))}…`;
}

export function MatrixHeatmap({
  rows,
  xKey,
  yKey,
  valueKey,
  valueFormat,
  cellMinWidth,
}: MatrixHeatmapProps) {
  const { xLabels, yLabels, lookup, valueMax } = useMemo(() => {
    const xs: string[] = [];
    const ys: string[] = [];
    const xSeen = new Set<string>();
    const ySeen = new Set<string>();
    const map = new Map<string, CellInfo>();
    let max = Number.NEGATIVE_INFINITY;
    for (const row of rows) {
      const xValue = toCategoryKey(row[xKey]);
      const yValue = toCategoryKey(row[yKey]);
      if (!xSeen.has(xValue)) {
        xSeen.add(xValue);
        xs.push(xValue);
      }
      if (!ySeen.has(yValue)) {
        ySeen.add(yValue);
        ys.push(yValue);
      }
      const numeric = toFiniteNumber(row[valueKey]);
      if (numeric !== null && numeric > max) max = numeric;
      // Use a delimiter that can't legally appear in a Postgres text
      // column so concatenated row/col keys are unambiguous.
      map.set(`${yValue}\u0001${xValue}`, {
        row: yValue,
        col: xValue,
        value: numeric,
        display: row[valueKey],
      });
    }
    if (!Number.isFinite(max)) max = 0;
    return { xLabels: xs, yLabels: ys, lookup: map, valueMax: max };
  }, [rows, xKey, yKey, valueKey]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hover, setHover] = useState<HoverState | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    setContainerWidth(node.clientWidth || FALLBACK_WIDTH);
    // jsdom and older browsers without ResizeObserver still need to
    // render — fall back to a single measurement so tests can assert
    // on the cell grid without depending on layout APIs.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width || FALLBACK_WIDTH);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const colCount = xLabels.length;
  const rowCount = yLabels.length;

  if (colCount === 0 || rowCount === 0) {
    return (
      <div className={styles.matrixHeatmapWrap} ref={containerRef}>
        <p className={styles.matrixHeatmapEmpty}>Heatmap needs at least one row of data.</p>
      </div>
    );
  }

  const minCell = Math.max(8, cellMinWidth ?? DEFAULT_MIN_CELL);
  const effectiveWidth = containerWidth || FALLBACK_WIDTH;
  const usableWidth = Math.max(minCell * colCount, effectiveWidth - Y_AXIS_WIDTH - RIGHT_PAD);
  const rawCellWidth = (usableWidth - (colCount - 1) * CELL_GAP) / Math.max(colCount, 1);
  const cellWidth = Math.max(minCell, Math.floor(rawCellWidth));
  const cellHeight = Math.min(MAX_CELL_HEIGHT, Math.max(MIN_CELL_HEIGHT, cellWidth));

  const rotateXLabels = colCount > ROTATE_THRESHOLD;
  const xLabelArea = rotateXLabels ? X_LABEL_HEIGHT_ROTATED : X_LABEL_HEIGHT;

  const gridWidth = colCount * cellWidth + Math.max(0, colCount - 1) * CELL_GAP;
  const gridHeight = rowCount * cellHeight + Math.max(0, rowCount - 1) * CELL_GAP;

  const svgWidth = Y_AXIS_WIDTH + gridWidth + RIGHT_PAD;
  const svgHeight = TOP_PAD + gridHeight + X_LABEL_MARGIN + xLabelArea;

  return (
    <div className={styles.matrixHeatmapWrap} ref={containerRef}>
      <div className={styles.matrixHeatmapScroll}>
        <svg
          className={styles.matrixHeatmapSvg}
          width={svgWidth}
          height={svgHeight}
          role="img"
          aria-label={`${yKey} by ${xKey} heatmap`}
          onPointerLeave={() => setHover(null)}
        >
          {yLabels.map((label, rowIdx) => {
            const cy = TOP_PAD + rowIdx * (cellHeight + CELL_GAP) + cellHeight / 2;
            const display = truncate(label || "—", ROW_LABEL_MAX_CHARS);
            return (
              <text
                key={`y-${rowIdx}-${label}`}
                x={Y_AXIS_WIDTH - 8}
                y={cy}
                textAnchor="end"
                dominantBaseline="central"
                className={styles.matrixHeatmapAxisLabel}
              >
                {display}
                <title>{label || "—"}</title>
              </text>
            );
          })}

          {xLabels.map((label, colIdx) => {
            const cx = Y_AXIS_WIDTH + colIdx * (cellWidth + CELL_GAP) + cellWidth / 2;
            const cy = TOP_PAD + gridHeight + X_LABEL_MARGIN;
            const display = truncate(label || "—", COL_LABEL_MAX_CHARS);
            if (rotateXLabels) {
              return (
                <text
                  key={`x-${colIdx}-${label}`}
                  x={cx}
                  y={cy}
                  textAnchor="end"
                  dominantBaseline="hanging"
                  transform={`rotate(-30 ${cx} ${cy})`}
                  className={styles.matrixHeatmapAxisLabel}
                >
                  {display}
                  <title>{label || "—"}</title>
                </text>
              );
            }
            return (
              <text
                key={`x-${colIdx}-${label}`}
                x={cx}
                y={cy}
                textAnchor="middle"
                dominantBaseline="hanging"
                className={styles.matrixHeatmapAxisLabel}
              >
                {display}
                <title>{label || "—"}</title>
              </text>
            );
          })}

          {yLabels.map((yLabel, rowIdx) =>
            xLabels.map((xLabel, colIdx) => {
              const cell = lookup.get(`${yLabel}\u0001${xLabel}`);
              const value = cell?.value ?? null;
              // Normalize against the dataset max — densities read more
              // intuitively when "0" maps to the lightest bucket and the
              // single hottest cell maps to the darkest, regardless of
              // whether the dataset's min happens to be 0 or 5.
              const normalized =
                value === null || valueMax <= 0 ? 0 : value / valueMax;
              const fill = value === null ? INTENSITY_PALETTE[0] : intensityColor(normalized);
              const cellX = Y_AXIS_WIDTH + colIdx * (cellWidth + CELL_GAP);
              const cellY = TOP_PAD + rowIdx * (cellHeight + CELL_GAP);
              const isHovered =
                hover !== null && hover.cell.col === xLabel && hover.cell.row === yLabel;
              return (
                <rect
                  key={`cell-${rowIdx}-${colIdx}`}
                  x={cellX}
                  y={cellY}
                  width={cellWidth}
                  height={cellHeight}
                  fill={fill}
                  className={
                    isHovered ? styles.matrixHeatmapCellHover : styles.matrixHeatmapCell
                  }
                  data-testid="matrix-heatmap-cell"
                  onPointerMove={(event) => {
                    const node = containerRef.current;
                    if (!node) return;
                    const bounds = node.getBoundingClientRect();
                    setHover({
                      cell: {
                        row: yLabel,
                        col: xLabel,
                        value,
                        display: cell?.display ?? null,
                      },
                      x: event.clientX - bounds.left,
                      y: event.clientY - bounds.top,
                    });
                  }}
                  onPointerLeave={() => setHover(null)}
                />
              );
            }),
          )}
        </svg>
      </div>

      {hover ? (
        <div
          className={styles.matrixHeatmapTooltip}
          role="tooltip"
          style={{
            // Pin to the cursor but clamp so the floating div can't leak
            // past the right edge of the widget container.
            left: Math.max(
              0,
              Math.min(hover.x + 12, Math.max(0, effectiveWidth - TOOLTIP_WIDTH - 4)),
            ),
            top: Math.max(0, hover.y - 56),
          }}
        >
          <div className={styles.matrixHeatmapTooltipLabel}>
            <span className={styles.matrixHeatmapTooltipKey}>{yKey}:</span>{" "}
            <span>{hover.cell.row || "—"}</span>
            <span className={styles.matrixHeatmapTooltipSeparator}>·</span>
            <span className={styles.matrixHeatmapTooltipKey}>{xKey}:</span>{" "}
            <span>{hover.cell.col || "—"}</span>
          </div>
          <div className={styles.matrixHeatmapTooltipValue}>
            {hover.cell.value === null
              ? "—"
              : formatValue(hover.cell.display, { format: valueFormat })}
          </div>
        </div>
      ) : null}

      <HeatmapLegend valueMax={valueMax} valueFormat={valueFormat} />
    </div>
  );
}

interface HeatmapLegendProps {
  valueMax: number;
  valueFormat: ValueFormat;
}

function HeatmapLegend({ valueMax, valueFormat }: HeatmapLegendProps) {
  // The cell color encoding is `value / valueMax` (zero-anchored), so
  // the lightest tint always represents 0 — never the dataset minimum.
  // Labeling the left end with `valueMin` (e.g. 80 for a tightly
  // clustered dataset) would imply that 80 maps to the lightest color
  // when it actually normalizes to 0.8 and renders near the darkest
  // end. Keep the legend honest by showing the true normalization
  // origin on the left and the dataset max on the right.
  return (
    <div className={styles.matrixHeatmapLegend}>
      <span className={styles.matrixHeatmapLegendLabel}>
        {formatValue(0, { format: valueFormat })}
      </span>
      <div className={styles.matrixHeatmapLegendStrip} aria-hidden>
        {INTENSITY_PALETTE.map((color, idx) => (
          <span
            key={idx}
            className={styles.matrixHeatmapLegendStop}
            style={{ background: color }}
          />
        ))}
      </div>
      <span className={styles.matrixHeatmapLegendLabel}>
        {formatValue(valueMax, { format: valueFormat })}
      </span>
    </div>
  );
}
